import React from 'react';
import { useTranslation } from 'react-i18next';
import type { Tenant } from '../../services/acceoConnectService';
import { TranslationKey } from '../../i18n/translationKeys';
import { Banner } from '../../ui/Banner/Banner';

interface TenantPickerProps {
    tenants: Tenant[];
    onSelect: (tenant: Tenant) => void;
    /**
     * The list below is missing the companies that need AcceoConnect to resolve. Rendered as a warning
     * because the alternative is a company silently absent from the table, which reads as "I have lost
     * my access" rather than "a dependency is down".
     */
    connectUnavailable?: boolean;
}

export const TenantPicker: React.FC<TenantPickerProps> = ({ tenants, onSelect, connectUnavailable }) => {
    const { t } = useTranslation();

    return (
        <div className="flex items-center justify-center min-h-screen bg-surface px-xl">
            <div className="bg-primary-50 border border-line-subtle rounded-lg w-full max-w-lg p-xl">
                <h1 className="text-page-title text-content mb-xs" data-testid="tenant-picker-title">
                    {t(TranslationKey.TenantPicker.Title)}
                </h1>
                <p className="text-s text-content-secondary mb-l">
                    {t(TranslationKey.TenantPicker.Subtitle)}
                </p>
                {connectUnavailable && (
                    <Banner
                        tone="warning"
                        testId="tenant-picker-connect-unavailable"
                        className="mb-l"
                    >
                        {t(TranslationKey.TenantPicker.ConnectUnavailable)}
                    </Banner>
                )}
                <table className="w-full text-s">
                    <thead>
                        <tr className="border-b border-line-subtle text-left text-primary-700">
                            <th className="pb-s pr-l font-normal">{t(TranslationKey.TenantPicker.ColCode)}</th>
                            <th className="pb-s pr-l font-normal">{t(TranslationKey.TenantPicker.ColCompany)}</th>
                            <th className="pb-s font-normal">{t(TranslationKey.TenantPicker.ColUser)}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {tenants.map((tenant) => (
                            <tr
                                key={`${tenant.ciu}-${tenant.tenantCode}`}
                                data-testid={`tenant-picker-row-${tenant.ciu}-${tenant.tenantCode}`}
                                onClick={() => onSelect(tenant)}
                                className="border-b border-primary-200 hover:bg-primary-200 cursor-pointer transition-colors"
                            >
                                <td className="py-m pr-l text-primary-1100">{tenant.tenantCode}</td>
                                <td className="py-m pr-l text-primary-1250">{tenant.companyName}</td>
                                <td className="py-m text-primary-700">{tenant.username}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <p className="text-xs text-primary-600 mt-l text-right">
                    {t(TranslationKey.TenantPicker.RecordCount, { count: tenants.length })}
                </p>
            </div>
        </div>
    );
};
